import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import Page from 'components/page/Page';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import { withModal } from 'components/modal/ModalHOC';

import SOVReportPageConsumerInsightsInstagram from './instagram/SOVReportPageConsumerInsightsInstagram';
import SOVHeaderPageConsumerInsights from 'modules/insights/sov/components/header/consumer-insights/SOVHeaderPageConsumerInsights';

import { __t, applyTranslations } from 'utils/language';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { FIXED_TO_TOP, INSTAGRAM } from 'modules/insights/sov/utils/sov';
import { VIDEO_TUTORIAL_GALLERY_MODAL_OBJECT } from 'modules/insights/sov/utils/consumer-insights/sov-consumer-insights';

import './sov-report-page-consumer-insights.scss';

applyTranslations(require('modules/insights/sov/shared/sov-shared.i18n.json'));
applyTranslations(require('./sov-report-page-consumer-insights.i18n.json'));

import type { LineGraphEChartsObject, LocationWindow } from 'types';
import type {
  SOVQueryLocal,
  SOVReport,
  SOVReportType
} from 'modules/insights/sov/types';

type OwnProps = {
  location: LocationWindow,
  brandProfileId: number | undefined | null,
  report: SOVReport,
  reportId: number,
  reportType: SOVReportType,
  isReportSaved: boolean,
  graphInstance: LineGraphEChartsObject | undefined | null,
  sovTimeframeCanSelectPastTwoWeeks: boolean,
  onReportSavedText: () => void,
  onClearReportSavedTimer: () => void,
  onChartInstanceCallback: (a: LineGraphEChartsObject) => void
};

type StateProps = {
  queriesMerged: Array<SOVQueryLocal>
};

type Props = {
  // eslint-disable-next-line react/no-unused-prop-types
  dispatch: any // added by connect
} & OwnProps & StateProps;

const DivWithGalleryModal = withModal(<div />, {
  ...WITH_GALLERY_MODAL_CONFIG,
  positionOverride: FIXED_TO_TOP
});

const SHARED_MSG_KEY = 'sov_shared';
const MSG_KEY = 'sov_report_page_consumer_insights';

class SOVReportPageConsumerInsights extends React.PureComponent<Props> {
  getDocumentTitle(): string {
    const ci = __t(`${MSG_KEY}.labels.consumer_insights`);
    const sov = __t(`${SHARED_MSG_KEY}.labels.share_of_voice`);

    return `${ci} - ${sov}`;
  }

  renderVideoTutorialLink(): React.ReactNode | undefined | null {
    const { queriesMerged } = this.props;

    if (isEmpty(queriesMerged)) {
      return null;
    }

    return (
      <DivWithGalleryModal
        modalContentProps={{
          theme: 'white',
          items: [VIDEO_TUTORIAL_GALLERY_MODAL_OBJECT],
          currentPosition: 0
        }}
        className="sov-report-page-consumer-insights__video-tutorial"
      >
        <div className="sov-report-page-consumer-insights__video-tutorial-icon">
          <SVGDynamicIcon
            name="info-inside-circle-custom-bg-white-fg"
            width={16}
            height={16}
            customColor="blue"
          />
        </div>
        <div className="sov-report-page-consumer-insights__video-tutorial-text">
          {__t(`${MSG_KEY}.labels.video_tutorial`)}
        </div>
      </DivWithGalleryModal>
    );
  }

  renderPageHeader = (): JSX.Element => {
    const { report } = this.props;

    return (
      <div className="sov-report-page-consumer-insights__page-header">
        <SOVHeaderPageConsumerInsights
          reportType={report.reportType}
        />
        {this.renderVideoTutorialLink()}
      </div>
    );
  };

  renderInstagram(): React.ReactNode {
    const {
      location,
      brandProfileId,
      report,
      reportId,
      reportType,
      graphInstance,
      isReportSaved,
      sovTimeframeCanSelectPastTwoWeeks,
      onReportSavedText,
      onClearReportSavedTimer,
      onChartInstanceCallback
    } = this.props;

    return (
      <SOVReportPageConsumerInsightsInstagram
        location={location}
        brandProfileId={brandProfileId}
        report={report}
        reportId={reportId}
        reportType={reportType}
        graphInstance={graphInstance}
        isReportSaved={isReportSaved}
        sovTimeframeCanSelectPastTwoWeeks={sovTimeframeCanSelectPastTwoWeeks}
        onRenderPageHeader={this.renderPageHeader}
        onReportSavedText={onReportSavedText}
        onClearReportSavedTimer={onClearReportSavedTimer}
        onChartInstanceCallback={onChartInstanceCallback}
      />
    );
  }

  render(): React.ReactNode {
    const { reportType } = this.props;

    return (
      <Page
        documentTitle={this.getDocumentTitle()}
      >
        <div className="sov-report-page sov-report-page-consumer-insights">
          {
            reportType === INSTAGRAM
              && this.renderInstagram()
          }
        </div>
      </Page>
    );
  }
}

const mapStateToProps = ({ sovQueries }): StateProps => ({
  queriesMerged: sovQueries.queriesMerged
});

export default connect(mapStateToProps)(SOVReportPageConsumerInsights);
